import React, { useEffect, useState } from 'react';
import { Loader2, ExternalLink, PlayCircle, Eye, AlertTriangle, ArrowLeft } from 'lucide-react';
import { fmtRelative, domainOf, youtubeThumb, extractUrls, normalizeUrl } from '../lib/utils';
import { getPublicDocument, getPublicDocumentUrl } from '../lib/api';
import { sourceMeta } from '../lib/sourceMeta';
import { useLanguage } from '../lib/LanguageContext';
import PdfViewer from './PdfViewer';
import ImageViewer from './ImageViewer';

/** Public, read-only view of a single shared note. No login required. */
export default function PublicNoteView({ owner, docId }) {
  const { t, language } = useLanguage();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState('');
  const [pdfOpen, setPdfOpen] = useState(false);
  const [imageOpen, setImageOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await getPublicDocument(owner, docId);
        if (!cancelled) setDoc(d);
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.response?.status === 404
              ? t('publicView.noteUnavailable')
              : e?.response?.data?.detail || e.message || t('publicView.noteLoadFailed'),
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner, docId]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-keep-page px-4">
        <div className="flex flex-col items-center gap-3 text-center" data-testid="public-note-error">
          <AlertTriangle className="w-8 h-8 text-keep-textMuted" />
          <p className="text-[15px] text-keep-text">{error}</p>
          <a href="/" className="text-[13px] text-keep-urlBlue hover:underline">
            {t('publicView.goToApp')}
          </a>
        </div>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-keep-page">
        <Loader2 className="w-8 h-8 text-keep-accent animate-spin" data-testid="public-note-loading" />
      </div>
    );
  }

  const isText = doc.source_type === 'text';
  const isPdf = doc.source_type === 'pdf';
  const isImage = doc.source_type === 'image';
  const isYoutubeT = doc.source_type === 'youtube';
  const isVideoPlatform = ['youtube', 'tiktok', 'instagram'].includes(doc.source_type);
  const { Icon, color, labelKey } = sourceMeta(doc.source_type);
  const ytThumb = isYoutubeT ? doc.preview?.image || youtubeThumb(doc.source_url) : '';
  const heroImage = isYoutubeT ? ytThumb : doc.preview?.image;
  const bodyText = isText ? doc.body_text || doc.summary || '' : doc.reason || '';
  const noteLinks = isText ? extractUrls(bodyText) : [];

  return (
    <div className="min-h-screen bg-keep-page">
      <header className="border-b border-keep-border bg-keep-surface px-4 py-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {typeof window !== 'undefined' &&
            window.history.length > 1 &&
            document.referrer &&
            document.referrer.startsWith(window.location.origin) && (
              <button
                onClick={() => window.history.back()}
                className="p-1.5 -ml-1.5 rounded-full hover:bg-keep-hover flex-shrink-0"
                aria-label={t('common.back')}
                data-testid="public-note-back"
              >
                <ArrowLeft className="w-4.5 h-4.5 text-keep-textMuted" />
              </button>
            )}
          <a href="/" className="flex items-center gap-2 min-w-0">
            <img src="/logo192.png" alt="Recycle Brain" className="w-7 h-7 rounded-lg flex-shrink-0" />
            <span className="text-[15px] font-medium text-keep-text truncate">Recycle Brain</span>
          </a>
        </div>
        <span className="text-[11px] text-keep-textFaint uppercase tracking-wide flex-shrink-0">
          {t('publicView.sharedNote')}
        </span>
      </header>

      <main className="max-w-2xl mx-auto px-3 md:px-4 py-6 md:py-10">
        <div
          className="bg-keep-surface border border-keep-border rounded-lg shadow-keep"
          data-testid="public-note-view"
        >
          {heroImage && (
            <div className="relative">
              <img
                src={heroImage}
                alt={doc.title || ''}
                className="w-full max-h-[320px] object-cover rounded-t-lg border-b border-keep-border"
              />
              {isVideoPlatform && doc.source_url && (
                <a
                  href={doc.source_url}
                  target="_blank"
                  rel="noreferrer"
                  className="absolute inset-0 flex items-center justify-center"
                  title={isYoutubeT ? t('noteDetail.openOnYoutube') : t('noteDetail.openSource')}
                >
                  <PlayCircle className="w-16 h-16 text-white/95 drop-shadow-lg hover:scale-105 transition-transform" strokeWidth={1.5} />
                </a>
              )}
            </div>
          )}

          <div className="p-5">
            <div className="flex items-center gap-1.5 mb-3 text-[11px] uppercase tracking-wide text-keep-textMuted">
              <Icon className={`w-4 h-4 ${color}`} />
              {t(labelKey)}
              {doc.created_at && (
                <span className="text-keep-textFaint ml-2 normal-case tracking-normal">
                  {fmtRelative(doc.created_at, t, language)}
                </span>
              )}
            </div>

            {doc.title && (
              <h1 className="text-[20px] font-medium text-keep-text mb-3 leading-snug break-words">
                {doc.title}
              </h1>
            )}

            {bodyText && (
              <p className="text-[15px] text-keep-text leading-relaxed whitespace-pre-wrap break-words">
                {bodyText}
              </p>
            )}

            {noteLinks.length > 1 && (
              <div className="mt-4 pt-4 border-t border-keep-border space-y-1.5" data-testid="public-note-sources">
                <div className="text-[11px] uppercase tracking-wider text-keep-textMuted">{t('common.sources')}</div>
                {noteLinks.map((u, i) => (
                  <a
                    key={i}
                    href={normalizeUrl(u)}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1.5 text-[14px] text-keep-urlBlue hover:underline break-all"
                  >
                    <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="break-all">{domainOf(u) || u}</span>
                  </a>
                ))}
              </div>
            )}

            {isPdf && (
              <button
                onClick={() => setPdfOpen(true)}
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[14px] bg-keep-accent text-keep-onAccent hover:opacity-90"
                data-testid="public-note-view-pdf"
              >
                <Eye className="w-4 h-4" />
                {t('noteDetail.viewPdf')}
              </button>
            )}

            {isImage && (
              <button
                onClick={() => setImageOpen(true)}
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[14px] bg-keep-accent text-keep-onAccent hover:opacity-90"
                data-testid="public-note-view-image"
              >
                <Eye className="w-4 h-4" />
                {t('noteDetail.viewImage')}
              </button>
            )}

            {doc.source_url && !isPdf && (
              <a
                href={doc.source_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 mt-4 text-[14px] text-keep-urlBlue hover:underline break-all"
              >
                <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="break-all">{domainOf(doc.source_url) || doc.source_url}</span>
              </a>
            )}
          </div>
        </div>

        <div
          className="mt-6 bg-keep-surface border border-keep-border rounded-lg p-5 flex flex-col items-center text-center gap-2"
          data-testid="public-note-cta"
        >
          <img src="/logo192.png" alt="Recycle Brain" className="w-9 h-9 rounded-lg mb-1" />
          <p className="text-[15px] font-medium text-keep-text">{t('publicView.ctaTitle')}</p>
          <p className="text-[13px] text-keep-textMuted max-w-sm">{t('publicView.ctaBody')}</p>
          <a
            href="/"
            className="mt-2 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-[14px] font-medium bg-keep-accent text-keep-onAccent hover:opacity-90 transition-colors"
            data-testid="public-note-cta-login"
          >
            {t('publicView.ctaButton')}
          </a>
        </div>
      </main>

      {pdfOpen && (
        <PdfViewer
          docId={doc.id}
          title={doc.title}
          onClose={() => setPdfOpen(false)}
          fetchUrl={() => getPublicDocumentUrl(owner, docId)}
        />
      )}
      {imageOpen && (
        <ImageViewer
          docId={doc.id}
          title={doc.title}
          onClose={() => setImageOpen(false)}
          fetchUrl={() => getPublicDocumentUrl(owner, docId)}
        />
      )}
    </div>
  );
}
